import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';

/** Primitive values which can not be translated into reactive form controls */
type ReactiveFormPrimitive = string | number | boolean | Date | null | undefined;

/**
 * Converts a generic type {@type T} into a FormGroup object. Each property in the FormGroup will be converted
 * into a reactive form object (FormArray, FormGroup, FormControl) based on that properties typing.
 */
export class ReactiveFormGroup<T> extends FormGroup<{
  [K in keyof T]: T[K] extends ReactiveFormPrimitive
    ? ReactiveFormControl<T[K]>
    : T[K] extends Array<unknown>
      ? ReactiveFormArray<T[K]>
      : T[K] extends object
        ? ReactiveFormGroup<T[K]>
        : AbstractControl<T[K]>;
}> {
  constructor(initialValue: T) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    super(<any>ReactiveFormGroup.createControls(initialValue));
  }

  /**
   * Creates an object of key properties and AbstractControl values matching the shape of the provided {@param value} object.
   *
   * @param value The object to transform into a reactive form object.
   */
  private static createControls<T>(value: T): { [K in keyof T]: AbstractControl } {
    const controls: Partial<{ [K in keyof T]: AbstractControl }> = {};

    for (const key in value) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) {
        continue;
      }

      const fieldValue: unknown = value[key];

      if (this.isPrimitive(fieldValue)) {
        controls[key] = new FormControl(fieldValue);
      } else if (Array.isArray(fieldValue)) {
        if (key === 'conditionCategories') {
          controls[key] = new FormControl(fieldValue);
        } else {
          controls[key] = new ReactiveFormArray(fieldValue);
        }
      } else if (typeof fieldValue === 'object') {
        controls[key] = new ReactiveFormGroup(fieldValue);
      }
    }
    return controls as { [K in keyof T]: AbstractControl };
  }

  /**
   * Determines if a provided value is a primitive type. A primitive type is any non-array, non key/value pair value.
   *
   * @param value The value to evaluate as a primitive.
   */
  private static isPrimitive(value: unknown): value is ReactiveFormPrimitive {
    return (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === undefined ||
      value === null ||
      value instanceof Date
    );
  }
}

/**
 * Converts a generic type {@type T} into a FormArray object. Each property in the FormGroup will be converted
 * into a reactive form object (FormArray, FormGroup, FormControl) based on that properties typing.
 */
export class ReactiveFormArray<T extends Array<unknown>> extends FormArray<
  T extends Array<infer U>
    ? U extends ReactiveFormPrimitive
      ? ReactiveFormControl<U>
      : U extends object
        ? ReactiveFormGroup<U>
        : AbstractControl<U>
    : never
> {
  constructor(initialValue: T) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    super(<any>ReactiveFormArray.createControls(initialValue));
  }

  /**
   * Creates an object of key properties and AbstractControl values matching the shape of the provided {@param value} object.
   *
   * @param value The object to transform into a reactive form object.
   */
  private static createControls<T extends Array<unknown>>(value: T): Array<AbstractControl> {
    return value.map((item): AbstractControl => {
      if (this.isPrimitive(item)) {
        return new FormControl(item);
      } else if (Array.isArray(item)) {
        return new ReactiveFormArray(item);
      } else if (typeof item === 'object') {
        return new ReactiveFormGroup(item);
      }
      throw new Error('Unsupported type');
    });
  }

  /**
   * Determines if a provided value is a primitive type. A primitive type is any non-array, non key/value pair value.
   *
   * @param value The value to evaluate as a primitive.
   */
  private static isPrimitive(value: unknown): value is ReactiveFormPrimitive {
    return (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === undefined ||
      value === null ||
      value instanceof Date
    );
  }
}

/**
 * Converts a generic type {@type T} into a FormControl object.
 */
export type ReactiveFormControl<T extends ReactiveFormPrimitive> = FormControl<T>;
